import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import {
  Target,
  CheckCircle2,
  Zap,
  Clock,
  PackageCheck,
  Truck,
  MapPin,
  Users,
  RefreshCw,
  Inbox,
  TrendingUp,
  Activity,
  Building2,
  BarChart3,
  CalendarRange,
  Hourglass,
  ArrowUp,
  ArrowDown,
  Gauge,
  Banknote,
  Wallet,
  AlertTriangle,
} from "lucide-react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import useAuth from "hooks/useAuth";
import useTheme from "hooks/useTheme";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useUrlState from "../../../hooks/useUrlState";
import { SearchableSelect } from "../../../components/ui/SearchableSelect";
import { RelativeTime } from "../../../components/ui/RelativeTime";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import { dashboardService } from "../../../services/dashboardService";
import { getCodConfig } from "../../../api/billing";
import { formatMoney } from "../../../utils/formatters";
import {
  getPackagesByStatusCount,
  getClientPackagesByStatusCount,
  getAdminPackages,
} from "../../../api/packages";
import { getOperationTimezone } from "../../../utils/timezone";
import { orderTypeLabel } from "../../../utils/orderTypeLabel";
import { deliveredFromResume } from "../../../utils/packagesResume";
import "./newDashboard.scss";

dayjs.extend(isoWeek);

const TOP_N = 8;
const MAX_RANGE_DAYS = 92; // tope de seguridad para el número de llamadas
const FETCH_CONCURRENCY = 8;

// Paleta de gráficos derivada de los tokens del design-system (recharts pinta
// SVG, así que necesitamos colores concretos). Leemos los tripletes RGB de las
// variables CSS en vivo: como --status-* / --fg-muted / --border ya conmutan con
// la clase .dark, la paleta queda alineada al sistema y respeta dark mode sin
// hexes literales. Fallbacks por si se evalúa antes de montar (SSR).
const tokenRGB = (name) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return undefined;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v.replace(/\s+/g, ", ")})` : undefined;
};

const buildPalette = (isDark) => ({
  brand: tokenRGB(isDark ? "--brand-400" : "--brand-500") || "#F97316",
  success: tokenRGB("--status-success") || "#229E5C",
  error: tokenRGB("--status-error") || "#DC2626",
  warning: tokenRGB("--status-warning") || "#EA580C",
  info: tokenRGB("--status-info") || "#0084FF",
  neutral: tokenRGB("--status-neutral") || "#71717A",
  reverse: tokenRGB("--status-reverse") || "#9333EA",
  cyan: tokenRGB("--status-cc") || "#0891B2",
  tick: tokenRGB("--fg-muted") || "#666666",
  grid: tokenRGB("--border") || "#E3E3E3",
});

// ── Pago contra entrega (COD) ────────────────────────────────────────────────
// Catálogo canónico: microservice-packages/src/api/packages/constants/
// cash-on-delivery.constants.ts. Acá viven solo el orden de presentación, las
// etiquetas y el color; el cálculo de qué entra en la recaudación (`settled`) lo
// hace el backend, el front no lo re-deriva.
const COD_METHOD_ORDER = ["cash", "transfer_driver", "transfer_seller"];

// Medios en los que el dinero pasa por el repartidor y por lo tanto el operador
// recauda. `transfer_seller` va directo al comercio: se grafica, pero no suma.
const COD_SETTLED_METHODS = ["cash", "transfer_driver"];

const codMethodLabel = (method, t) => {
  if (method === "cash") return t('Efectivo');
  if (method === "transfer_driver") return t('Transferencia al repartidor');
  if (method === "transfer_seller") return t('Transferencia al vendedor');
  return method || t('Sin medio');
};

const codMethodColor = (method, p) => {
  if (method === "cash") return p.success;
  if (method === "transfer_driver") return p.info;
  // Neutro a propósito: es el único que el operador NO recauda.
  if (method === "transfer_seller") return p.neutral;
  return p.reverse; // medio nuevo que este front todavía no conoce
};

const capitalizeWords = (str) =>
  !str ? "—" : str.replace(/\b\w/g, (c) => c.toUpperCase());

const truncate = (str, max = 16) => {
  const s = capitalizeWords(str);
  return s.length > max ? s.substring(0, max) + "…" : s;
};

const slColor = (pct, p) => {
  if (pct >= 90) return p.success;
  if (pct >= 80) return p.brand;
  if (pct >= 70) return p.warning;
  return p.error;
};

const msToHHMM = (ms) => {
  const minutes = ms > 0 ? Math.round(ms / 60000) : 0;
  return `${Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0")}:${(minutes % 60).toString().padStart(2, "0")}`;
};

// Minutos → "HH:MM" (driver-performance entrega tiempos en minutos, no ms).
const minutesToHHMM = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const m = Math.max(0, Math.round(Number(minutes)));
  return `${Math.floor(m / 60)
    .toString()
    .padStart(2, "0")}:${(m % 60).toString().padStart(2, "0")}`;
};

// Etiqueta corta de semana ISO: "S23 02/06".
const formatWeekLabel = (weekStart) => {
  const d = dayjs(weekStart);
  return `S${d.isoWeek()} ${d.format("DD/MM")}`;
};

const pctOf = (part, total) =>
  total > 0 ? Math.round((part / total) * 1000) / 10 : 0;

// Color del % de gestión / nivel (umbrales consistentes con slColor).
const pctTone = (pct, p) => {
  if (pct >= 80) return p.success;
  if (pct >= 50) return p.warning;
  return p.error;
};

// Lista de días "YYYY-MM-DD" en el rango [start, end] (inclusivo, con tope).
const eachDay = (start, end) => {
  const out = [];
  let d = dayjs(start);
  const last = dayjs(end);
  let guard = 0;
  while ((d.isBefore(last) || d.isSame(last, "day")) && guard < MAX_RANGE_DAYS) {
    out.push(d.format("YYYY-MM-DD"));
    d = d.add(1, "day");
    guard += 1;
  }
  return out;
};

// Trae operational-summary de cada día en lotes paralelos (Redis-cacheado en back).
// Devuelve pares { date, data } para conservar la fecha de cada día (la usa la
// curva de tendencia); los días sin respuesta se descartan.
async function fetchOperationalRange(dates, lc, tz) {
  const results = [];
  for (let i = 0; i < dates.length; i += FETCH_CONCURRENCY) {
    const slice = dates.slice(i, i + FETCH_CONCURRENCY);
    // eslint-disable-next-line no-await-in-loop
    const resp = await Promise.all(
      slice.map((d) =>
        dashboardService
          .getOperationalSummary(lc, d, tz)
          .then((r) => ({ date: d, data: r.data }))
          .catch(() => null)
      )
    );
    resp.forEach((r) => r && r.data && results.push(r));
  }
  return results;
}

// Serie diaria de nivel de servicio y atención mismo día para la curva de
// tendencia. Un punto por día con respuesta; los % se leen tal cual del back.
function buildServiceLevelSeries(pairs) {
  return pairs
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map(({ date, data }) => {
      // Días sin entregas/recolección no tienen NS real: van como null (gap)
      // para que la curva los puentee en vez de desplomarse a 0% (engañoso).
      const slTotal = data?.serviceLevel?.total || 0;
      const sdTotal = data?.sameDayAttention?.totalCollected || 0;
      return {
        date,
        label: dayjs(date).format("DD MMM"),
        sl: slTotal > 0 ? Math.round((data.serviceLevel.serviceLevelPct || 0) * 10) / 10 : null,
        attention:
          sdTotal > 0 ? Math.round((data.sameDayAttention.attentionPct || 0) * 10) / 10 : null,
      };
    });
}

// Ventana horaria del gráfico de franjas. Fija a propósito: mantiene el eje
// comparable entre días y evita arrastrar franjas muertas de madrugada. Lo que
// caiga fuera NO se grafica, así que el total de la serie de recolecciones puede
// quedar por debajo del KPI "Paquetes recolectados", que cuenta el día completo.
const SLOT_WINDOW_START = "08:00";
const SLOT_WINDOW_END = "23:59";

const SLOT_MINUTES = 30;
const slotToMinutes = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const minutesToSlot = (m) =>
  `${Math.floor(m / 60)
    .toString()
    .padStart(2, "0")}:${(m % 60).toString().padStart(2, "0")}`;

// Acumula las dos series por HH:mm a lo largo de todos los días del rango.
// Devuelve Map<"HH:mm", { delivered, collected }>.
function accumulateHalfHourSlots(days, rangeSet) {
  const slots = new Map();

  const add = (ts, count, key) => {
    if (!(count > 0)) return;
    const t = dayjs(ts).format("HH:mm");
    const dd = dayjs(ts).format("YYYY-MM-DD");
    if (t < SLOT_WINDOW_START || t > SLOT_WINDOW_END || !rangeSet.has(dd)) return;
    const prev = slots.get(t) || { delivered: 0, collected: 0 };
    prev[key] += count;
    slots.set(t, prev);
  };

  days.forEach((d) => {
    Object.entries(d?.deliveriesByHalfHour || {}).forEach(([ts, c]) => add(ts, c, "delivered"));
    // Ausente mientras el backend no exponga la serie (o con caché previa al deploy):
    // la línea de recolecciones queda en 0 en vez de romper el gráfico.
    Object.entries(d?.collectionsByHalfHour || {}).forEach(([ts, c]) => add(ts, c, "collected"));
  });

  return slots;
}

// Serie continua de 30 en 30 min entre el primer y el último bucket con actividad.
// Los huecos intermedios van EXPLÍCITOS en 0: recharts trata `time` como categoría,
// así que omitirlos pinta dos buckets distantes como adyacentes y las dos series
// dejan de ser comparables entre sí. La grilla se une con las claves reales para
// que ningún bucket recibido quede fuera del barrido.
//
// `collectedCumulative` es la suma corrida de las recolecciones a lo largo de la
// serie: responde "a esta hora, ¿cuánto llevo recibido?". Depende del orden, así
// que se calcula DESPUÉS de ordenar la grilla, no sobre el Map. Arranca en el
// primer bucket de la grilla, que puede ser anterior a la primera recolección
// (si ese bucket lo abrió una entrega): la curva queda plana en 0 hasta que entra
// la primera, que es exactamente lo que pasó.
function buildHalfHourSeries(slots) {
  const times = [...slots.keys()].sort();
  if (times.length === 0) return [];

  const startMin = Math.floor(slotToMinutes(times[0]) / SLOT_MINUTES) * SLOT_MINUTES;
  const endMin = slotToMinutes(times[times.length - 1]);

  const grid = new Set(times);
  for (let m = startMin; m <= endMin; m += SLOT_MINUTES) grid.add(minutesToSlot(m));

  let runningCollected = 0;
  return [...grid].sort().map((time) => {
    const v = slots.get(time) || { delivered: 0, collected: 0 };
    runningCollected += v.collected;
    return {
      time,
      delivered: v.delivered,
      collected: v.collected,
      collectedCumulative: runningCollected,
    };
  });
}

// Fusiona N días de operational-summary en un único objeto con la MISMA forma.
// Los porcentajes se RECALCULAN desde los totales subyacentes (no se promedian).
function mergeOperational(days, rangeSet, p) {
  const sum = (f) => days.reduce((a, d) => a + (Number(f(d)) || 0), 0);
  const pct = (part, total) => (total > 0 ? (part / total) * 100 : 0);

  const delivered = sum((d) => d?.packagesPercentage?.delivered);
  const total = sum((d) => d?.packagesPercentage?.total);

  const slTotal = sum((d) => d?.serviceLevel?.total);
  const slBefore = sum((d) => d?.serviceLevel?.deliveredBeforeCutoff);

  const sdTotal = sum((d) => d?.sameDayAttention?.totalCollected);
  const sdBefore = sum((d) => d?.sameDayAttention?.sameDayBeforeCutoff);

  // Tiempo promedio: ponderado por entregas del día.
  let wSum = 0;
  let wTot = 0;
  days.forEach((d) => {
    const t = d?.averageDeliveryTime?.averageDeliveryTime || 0;
    const w = d?.packagesPercentage?.delivered || 0;
    if (t > 0 && w > 0) {
      wSum += t * w;
      wTot += w;
    }
  });

  // Recolectados por cliente (merge por nombre).
  const senderMap = new Map();
  days.forEach((d) =>
    (d?.packagesCollected?.senders || []).forEach((s) => {
      senderMap.set(s.businessName, (senderMap.get(s.businessName) || 0) + (s.count || 0));
    })
  );

  // NS por tipo (merge por tipo, recalcula pct).
  const typeMap = new Map();
  days.forEach((d) =>
    (d?.serviceLevelByType || []).forEach((it) => {
      const prev = typeMap.get(it.type) || { total: 0, before: 0 };
      prev.total += it.total || 0;
      prev.before += it.deliveredBeforeCutoff || 0;
      typeMap.set(it.type, prev);
    })
  );

  // Recolectados por comuna (merge por comuna).
  const muniMap = new Map();
  days.forEach((d) =>
    (d?.collectedByMunicipality || []).forEach((it) => {
      muniMap.set(it.municipality, (muniMap.get(it.municipality) || 0) + (it.count || 0));
    })
  );

  // Franja horaria: acumula por HH:mm a través de todos los días del rango. Dos
  // series sobre el MISMO eje: entregas (deliveredAt) y recolecciones (collectDate).
  // El backend rellena en 0 la franja 08:00–23:30, así que un bucket ausente en un
  // mapa y presente en el otro solo puede venir de fuera de esa franja.
  const slotMap = accumulateHalfHourSlots(days, rangeSet);
  const halfHour = buildHalfHourSeries(slotMap);

  // En ruta = estado en tiempo real (no depende de la fecha): tomamos el día más reciente.
  const latest = days[days.length - 1] || {};

  return {
    packagesPercentage: { delivered, total, percentage: pct(delivered, total) },
    serviceLevel: {
      total: slTotal,
      deliveredBeforeCutoff: slBefore,
      serviceLevelPct: pct(slBefore, slTotal),
    },
    sameDayAttention: {
      totalCollected: sdTotal,
      sameDayBeforeCutoff: sdBefore,
      attentionPct: pct(sdBefore, sdTotal),
    },
    averageDeliveryTime: { averageDeliveryTime: wTot ? wSum / wTot : 0 },
    packagesCollected: {
      total: sum((d) => d?.packagesCollected?.total),
      senders: [...senderMap].map(([businessName, count]) => ({ businessName, count })),
    },
    serviceLevelByType: [...typeMap].map(([type, v]) => ({
      type,
      total: v.total,
      deliveredBeforeCutoff: v.before,
      serviceLevelPct: pct(v.before, v.total),
    })),
    collectedByMunicipality: [...muniMap]
      .map(([municipality, count]) => ({ municipality, count }))
      .sort((a, b) => b.count - a.count),
    inRouteByMunicipality: latest.inRouteByMunicipality || [],
    inRouteByType: latest.inRouteByType || [],
    halfHour,
  };
}

// ── Sub-componentes ──────────────────────────────────────────────────────────

const KpiTile = ({ icon: Icon, tone, value, label, sub }) => (
  <div className={`ndash-kpi ndash-kpi--${tone}`}>
    <span className="ndash-kpi__icon">
      <Icon size={18} strokeWidth={2.2} />
    </span>
    <div className="ndash-kpi__body">
      <span className="ndash-kpi__value">{value}</span>
      <span className="ndash-kpi__label">{label}</span>
      {sub && <span className="ndash-kpi__sub">{sub}</span>}
    </div>
  </div>
);

const Section = ({ title, hint, icon: Icon, children, className = "" }) => (
  <section className={`ndash-section ${className}`}>
    <header className="ndash-section__head">
      <h3 className="ndash-section__title">
        {Icon && <Icon size={16} strokeWidth={2.2} />}
        {title}
      </h3>
      {hint && <span className="ndash-section__hint">{hint}</span>}
    </header>
    <div className="ndash-section__body">{children}</div>
  </section>
);

const EmptyBlock = ({ label }) => (
  <div className="ndash-empty">
    <Inbox size={26} strokeWidth={1.6} />
    <span>{label}</span>
  </div>
);

// `showName`: antepone el nombre de cada serie. Necesario cuando el gráfico tiene
// más de una (si no, dos números sueltos no se distinguen salvo por el color).
// `detail(p)`: texto secundario opcional por serie. Sirve para exponer un dato del
// punto que NO se grafica (p. ej. el valor por franja detrás de una curva acumulada).
const ChartTooltip = ({ active, payload, label, suffix = "", showName = false, detail }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ndash-tooltip">
      {label != null && <div className="ndash-tooltip__label">{label}</div>}
      {payload.map((p, i) => {
        const extra = detail ? detail(p) : null;
        return (
          <div className="ndash-tooltip__row" key={i}>
            <span className="ndash-tooltip__dot" style={{ background: p.color || p.fill }} />
            {showName && p.name && <span className="ndash-tooltip__name">{p.name}</span>}
            <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
            {suffix}
            {extra && <span className="ndash-tooltip__extra">{extra}</span>}
          </div>
        );
      })}
    </div>
  );
};

// Barra horizontal apilada (gestionado + pendiente) para el ranking de volumen.
const StackBar = ({ managed, pending, max, colors }) => {
  const total = managed + pending;
  const w = (v) => (max > 0 ? (v / max) * 100 : 0);
  return (
    <div className="ndash-stack" title={`${total.toLocaleString()}`}>
      <div className="ndash-stack__seg ndash-stack__seg--managed" style={{ width: `${w(managed)}%`, background: colors.success }} />
      <div className="ndash-stack__seg ndash-stack__seg--pending" style={{ width: `${w(pending)}%`, background: colors.error }} />
    </div>
  );
};

// ── Vista principal ────────────────────────────────────────────────────────────

const NewDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const p = useMemo(() => buildPalette(isDark), [isDark]);
  const isMobile = useIsMobile();

  const logisticCompanyId = useMemo(
    () => user?.logisticCompany?._id || user?.logisticCompanyId || null,
    [user]
  );
  const ianaTz = useMemo(() => getOperationTimezone(user), [user]);

  // Preset de rango en la URL (?preset=, replace). El rango concreto (start/end)
  // vive en estado local y se hidrata desde el preset vía rangeFromPreset, así el
  // primer fetch ya queda sembrado de forma síncrona. Default 'today' ⇒ URL limpia.
  const [preset, setPreset] = useUrlState("preset", "today");
  // Rango custom en la URL (?from/?to) para que un preset 'custom' sobreviva
  // reload y back/forward; vacío ('') ⇒ se omite de la URL.
  const [customFrom, setCustomFrom] = useUrlState("from", "");
  const [customTo, setCustomTo] = useUrlState("to", "");
  const [range, setRange] = useState(() => {
    if (preset === "custom" && customFrom && customTo) {
      return { start: customFrom, end: customTo, preset };
    }
    const [s, e] = rangeFromPreset(preset);
    return { start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD"), preset };
  });
  const [op, setOp] = useState(null);
  const [slSeries, setSlSeries] = useState([]);
  const [statusCounts, setStatusCounts] = useState(null);
  // Secciones portadas del dashboard viejo (todas por rango, mismas fechas).
  const [clientVolumeTotals, setClientVolumeTotals] = useState(null); // { total, managed, pending }
  const [weekly, setWeekly] = useState(null); // { weeks: [...] }
  const [drivers, setDrivers] = useState(null); // { drivers, totals }
  const [pending, setPending] = useState(null); // { entries, total }
  // Pago contra entrega: resumen del rango + política del operador. `codEnabled`
  // en null = desconocida (la config no cargó); ver el useEffect de abajo.
  const [cod, setCod] = useState(null); // { currency, inRoute, collected, settled, byMethod }
  const [codEnabled, setCodEnabled] = useState(null); // true | false | null
  // Agrupación de pendientes en la URL (?pendingMode=). Default 'municipality' ⇒ URL limpia.
  const [pendingMode, setPendingMode] = useUrlState("pendingMode", "municipality"); // 'municipality' | 'client'
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const firstLoad = useRef(true);
  // Token por corrida de fetchData: invalida una corrida en vuelo cuando arranca
  // otra (cambio de rango/cliente, refresh manual, remount de StrictMode) o cuando
  // el componente se desmonta. Un `mountedRef` no alcanza: no distingue corridas
  // (la vieja pisaría los datos de la nueva, last-await-wins).
  const runRef = useRef(0);

  // Filtro por cliente. El back solo filtra por cliente el VOLUMEN (estado de
  // pedidos + total recolectado); NS/atención/operación siguen siendo globales
  // (operational-summary ignora companyId — ver gap en BEARTRACK-CONTEXT.md).
  const [clients, setClients] = useState([]); // [{ companyId, businessName, total, managed, pending }]
  // Cliente seleccionado en la URL (?client=<companyId>). Default '' ⇒ URL limpia.
  const [selectedClient, setSelectedClient] = useUrlState("client", ""); // companyId | ""
  const selectedClientRow = useMemo(
    () => clients.find((c) => c.companyId === selectedClient) || null,
    [clients, selectedClient]
  );
  const selectedClientName = selectedClientRow?.businessName || "";
  // Filtro por cliente activo: el back solo filtra volumen por cliente, así que
  // los KPIs de NS/atención/operación se marcan como "Global".
  const clientFiltered = Boolean(selectedClient);

  const fetchData = useCallback(
    async (manual = false) => {
      if (!logisticCompanyId) return;
      const run = ++runRef.current;
      if (manual || !firstLoad.current) setRefreshing(true);
      if (manual) setError(null);

      const dates = eachDay(range.start, range.end);
      const rangeSet = new Set(dates);

      try {
        const pairs = await fetchOperationalRange(dates, logisticCompanyId, ianaTz);
        if (runRef.current !== run) return;
        if (pairs.length === 0) throw new Error("Sin respuesta del servidor.");
        setOp(mergeOperational(pairs.map((x) => x.data), rangeSet, p));
        setSlSeries(buildServiceLevelSeries(pairs));
        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        if (runRef.current !== run) return;
        setError(
          err?.response?.data?.message || "No se pudieron cargar los datos del dashboard."
        );
      } finally {
        // Solo la corrida vigente toca los flags compartidos (loading/refreshing);
        // una corrida vieja no debe apagar el spinner de la que sigue en vuelo.
        if (runRef.current === run) {
          if (firstLoad.current) {
            firstLoad.current = false;
            setLoading(false);
          }
          setRefreshing(false);
        }
      }

      // Catálogo de clientes del período (volumen por cliente). Alimenta el
      // selector y, si hay cliente elegido, el KPI de volumen.
      try {
        const cvResp = await dashboardService.getClientVolume(
          logisticCompanyId,
          range.start,
          range.end,
          ianaTz
        );
        if (runRef.current !== run) return;
        const cvData = cvResp?.data?.data ?? cvResp?.data ?? {};
        const list = (cvData.clients || [])
          .filter((c) => c.companyId)
          .sort((a, b) => (b.total || 0) - (a.total || 0));
        setClients(list);
        setClientVolumeTotals(
          cvData.totals || {
            total: list.reduce((a, c) => a + (c.total || 0), 0),
            managed: list.reduce((a, c) => a + (c.managed || 0), 0),
            pending: list.reduce((a, c) => a + (c.pending || 0), 0),
          }
        );
        // Si el cliente seleccionado ya no está en el período, resetea a "Todos".
        setSelectedClient((prev) =>
          prev && !list.some((c) => c.companyId === prev) ? "" : prev
        );
      } catch {
        if (runRef.current !== run) return;
        setClients([]);
        setClientVolumeTotals(null);
      }

      // Comparativa semanal, performance de repartidores, pendientes (breakdown) y
      // pago contra entrega. Todas por el mismo rango; se piden en paralelo y cada
      // una tolera su error. cod-summary NO manda logisticCompanyId: lo deriva del
      // JWT en el backend (ver dashboardService.getCodSummary).
      const [weeklyResp, driversResp, pendingResp2, codResp] = await Promise.allSettled([
        dashboardService.getClientVolumeWeekly(logisticCompanyId, range.start, range.end, ianaTz),
        dashboardService.getDriverPerformance(logisticCompanyId, range.start, range.end, ianaTz),
        dashboardService.getPendingBreakdown(logisticCompanyId, range.start, range.end, ianaTz),
        dashboardService.getCodSummary(range.start, range.end, ianaTz),
      ]);
      if (runRef.current !== run) return;
      setWeekly(
        weeklyResp.status === "fulfilled"
          ? weeklyResp.value?.data?.data ?? weeklyResp.value?.data ?? null
          : null
      );
      setDrivers(
        driversResp.status === "fulfilled"
          ? driversResp.value?.data?.data ?? driversResp.value?.data ?? null
          : null
      );
      setPending(
        pendingResp2.status === "fulfilled"
          ? pendingResp2.value?.data?.data ?? pendingResp2.value?.data ?? null
          : null
      );
      setCod(
        codResp.status === "fulfilled"
          ? codResp.value?.data?.data ?? codResp.value?.data ?? null
          : null
      );

      // Conteo por estado. Por cliente usa el endpoint per-client; global usa
      // el resumen de la empresa + pendientes nativos.
      try {
        if (selectedClient) {
          const counts = await getClientPackagesByStatusCount({
            companyId: selectedClient,
            startDate: range.start,
            endDate: range.end,
          });
          if (runRef.current !== run) return;
          setStatusCounts({
            pending: counts?.status0 || 0,
            picked: counts?.status1 || 0,
            warehouse: 0, // el endpoint per-client no desglosa bodega
            route: counts?.status3_30 || 0,
            // Sigue contando por fecha de CREACIÓN (microservice-packages): no es el
            // mismo criterio que el global, que cuenta por fecha de entrega.
            delivered: counts?.status4 || 0,
            failed: counts?.status6_7 || 0,
            returns: 0, // ni devoluciones
          });
        } else {
          const [counts, pendingResp] = await Promise.all([
            getPackagesByStatusCount({
              logisticCompanyId,
              startDate: range.start,
              endDate: range.end,
            }),
            getAdminPackages({
              limit: 1,
              page: 1,
              status: [0],
              startDate: dayjs(range.start).format("MM/DD/YYYY"),
              endDate: dayjs(range.end).format("MM/DD/YYYY"),
              orderField: "updatedAt",
              sortType: "desc",
            }),
          ]);
          if (runRef.current !== run) return;
          setStatusCounts({
            pending: pendingResp?.data?.totalDocs || 0,
            picked: counts?.status1 || 0,
            warehouse: counts?.status2 || 0,
            route: counts?.status3_30 || 0,
            delivered: deliveredFromResume(counts),
            failed: counts?.status6_7 || 0,
            returns: counts?.status21_22 || 0,
          });
        }
      } catch {
        if (runRef.current !== run) return;
        setStatusCounts(null);
      }
    },
    [logisticCompanyId, range, ianaTz, p, selectedClient, setSelectedClient]
  );

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    fetchData();
    // Auto-refresh solo para "Hoy" (datos en vivo); los días/rangos pasados son estáticos.
    if (range.preset === "today") {
      const interval = setInterval(() => { if (!document.hidden) fetchData(false); }, 120000);
      return () => {
        clearInterval(interval);
        runRef.current++;
      };
    }
    return () => {
      runRef.current++;
    };
  }, [logisticCompanyId, range, fetchData]);

  // Política de pago contra entrega del operador (colección versionada
  // `codconfigs`). Va en su propio efecto y NO dentro de fetchData porque no
  // depende del rango: colgarla del refresco de "Hoy" pegaría a billing cada dos
  // minutos para leer un dato que solo cambia por acción manual del operador.
  // Si falla (403, red), queda en null = "no sé": la sección COD entonces solo
  // aparece si hay montos, nunca vacía.
  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let alive = true;
    getCodConfig()
      .then((res) => {
        if (!alive) return;
        const body = res?.data;
        const cfg =
          body && typeof body === "object" && !Array.isArray(body) && "data" in body
            ? body.data
            : body;
        setCodEnabled(cfg ? !!cfg.enabled : false);
      })
      .catch(() => {
        if (alive) setCodEnabled(null);
      });
    return () => {
      alive = false;
    };
  }, [logisticCompanyId]);

  const handleRange = (startYMD, endYMD, presetKey) => {
    firstLoad.current = true;
    setLoading(true);
    setError(null);
    setRange({ start: startYMD, end: endYMD, preset: presetKey });
    if (presetKey === "custom") {
      // Persistí el rango concreto además de ?preset=custom para reload/back.
      setCustomFrom(startYMD);
      setCustomTo(endYMD);
      setPreset("custom");
    } else {
      setCustomFrom("");
      setCustomTo("");
      setPreset(presetKey); // refleja el preset en la URL (replace)
    }
  };

  // Fuente única del preset: el DateRangeFilter usa value={preset} (URL). Cuando
  // el preset cambia (incluido back/forward y deep-link), re-derivamos el rango.
  // 'custom' se reconstruye desde ?from/?to; el resto desde rangeFromPreset.
  // Conservamos range.preset para que el auto-refresh de "Hoy" siga funcionando.
  // El guard por valor evita un fetch extra cuando el rango no cambió.
  useEffect(() => {
    if (preset === "custom") {
      if (customFrom && customTo) {
        setRange((prev) =>
          prev.start === customFrom && prev.end === customTo && prev.preset === "custom"
            ? prev
            : { start: customFrom, end: customTo, preset: "custom" }
        );
      }
      return;
    }
    const [s, e] = rangeFromPreset(preset);
    const start = s.format("YYYY-MM-DD");
    const end = e.format("YYYY-MM-DD");
    setRange((prev) =>
      prev.start === start && prev.end === end && prev.preset === preset
        ? prev
        : { start, end, preset }
    );
  }, [preset, customFrom, customTo]);

  // ── Datos derivados ──────────────────────────────────────────────────────
  const isSingleDay = range.start === range.end;
  const rangeLabel = useMemo(() => {
    const s = dayjs(range.start).format("DD/MM/YYYY");
    const e = dayjs(range.end).format("DD/MM/YYYY");
    return s === e ? s : `${s} – ${e}`;
  }, [range]);
  const periodWord = isSingleDay ? t('del día') : t('del período');

  const compliance = useMemo(() => {
    const d = op?.packagesPercentage;
    return {
      pct: Math.round((d?.percentage || 0) * 10) / 10,
      delivered: d?.delivered || 0,
      total: d?.total || 0,
    };
  }, [op]);

  const sl = useMemo(
    () => ({
      pct: Math.round((op?.serviceLevel?.serviceLevelPct || 0) * 10) / 10,
      before: op?.serviceLevel?.deliveredBeforeCutoff || 0,
      total: op?.serviceLevel?.total || 0,
    }),
    [op]
  );

  const sameDay = useMemo(
    () => ({
      pct: Math.round((op?.sameDayAttention?.attentionPct || 0) * 10) / 10,
      before: op?.sameDayAttention?.sameDayBeforeCutoff || 0,
      total: op?.sameDayAttention?.totalCollected || 0,
    }),
    [op]
  );

  const avgTime = useMemo(
    () => msToHHMM(op?.averageDeliveryTime?.averageDeliveryTime || 0),
    [op]
  );

  const collectedTotal = useMemo(() => op?.packagesCollected?.total || 0, [op]);
  const halfHourData = useMemo(() => op?.halfHour || [], [op]);

  const slByType = useMemo(
    () =>
      (op?.serviceLevelByType || [])
        .map((it) => ({
          name: capitalizeWords(orderTypeLabel(it.type)),
          pct: Math.round((it.serviceLevelPct || 0) * 10) / 10,
          total: it.total || 0,
          before: it.deliveredBeforeCutoff || 0,
        }))
        .sort((a, b) => b.total - a.total),
    [op]
  );

  const topCommunes = useMemo(
    () =>
      [...(op?.collectedByMunicipality || [])]
        .slice(0, TOP_N)
        .map((it) => ({
          name: truncate(it.municipality),
          fullName: capitalizeWords(it.municipality),
          count: it.count || 0,
        }))
        .reverse(),
    [op]
  );

  const driversInRoute = useMemo(() => {
    const map = new Map();
    (op?.inRouteByMunicipality || []).forEach((m) => {
      (m.drivers || []).forEach((d) => {
        const key = d.driverId || d.name;
        if (!key) return;
        const prev = map.get(key) || { name: d.name, count: 0 };
        prev.count += d.packageCount || 0;
        map.set(key, prev);
      });
    });
    return [...map.values()].sort((a, b) => b.count - a.count).slice(0, TOP_N);
  }, [op]);

  const totalInRoute = useMemo(
    () => (op?.inRouteByType || []).reduce((acc, it) => acc + (it.count || 0), 0),
    [op]
  );

  const statusData = useMemo(() => {
    if (!statusCounts) return [];
    const rows = [
      { key: "pending", name: t('Pendientes'), value: statusCounts.pending, color: p.neutral },
      { key: "picked", name: t('Recolectados'), value: statusCounts.picked, color: p.info },
      { key: "warehouse", name: t('En bodega'), value: statusCounts.warehouse, color: p.cyan },
      { key: "route", name: t('En ruta'), value: statusCounts.route, color: p.warning },
      { key: "delivered", name: t('Entregados'), value: statusCounts.delivered, color: p.success },
      { key: "failed", name: t('Entrega fallida'), value: statusCounts.failed, color: p.error },
      { key: "returns", name: t('Devoluciones'), value: statusCounts.returns, color: p.reverse },
    ];
    // El conteo per-client no desglosa bodega ni devoluciones: omitirlas en vez
    // de mostrar 0 (que se leería como "no hay", no como "sin dato"). Lo mismo
    // con "Entregados" cuando el backend no pudo contarlo (llega null).
    const withData = rows.filter((r) => r.value != null);
    return clientFiltered
      ? withData.filter((r) => r.key !== "warehouse" && r.key !== "returns")
      : withData;
  }, [statusCounts, p, clientFiltered]);

  const statusTotal = useMemo(
    () => statusData.reduce((acc, s) => acc + s.value, 0),
    [statusData]
  );

  const slGaugeColor = slColor(sl.pct, p);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.companyId, label: capitalizeWords(c.businessName) })),
    [clients]
  );

  // ── Volumen por cliente (ranking) ─────────────────────────────────────────
  const volumeRanking = useMemo(
    () =>
      clients
        .slice(0, TOP_N)
        .map((c) => ({
          companyId: c.companyId,
          name: capitalizeWords(c.businessName),
          total: c.total || 0,
          managed: c.managed || 0,
          pending: c.pending || 0,
          managedPct: pctOf(c.managed || 0, c.total || 0),
        })),
    [clients]
  );
  const volumeMax = useMemo(
    () => volumeRanking.reduce((m, c) => Math.max(m, c.total), 0),
    [volumeRanking]
  );
  const volumeTotals = useMemo(
    () =>
      clientVolumeTotals || {
        total: clients.reduce((a, c) => a + (c.total || 0), 0),
        managed: clients.reduce((a, c) => a + (c.managed || 0), 0),
        pending: clients.reduce((a, c) => a + (c.pending || 0), 0),
      },
    [clientVolumeTotals, clients]
  );

  // ── Comparativa semanal (agregada por semana del período) ─────────────────
  const weeklyData = useMemo(() => {
    const weeks = weekly?.weeks || [];
    return weeks.map((w, i, arr) => {
      const total = (w.clients || []).reduce((s, c) => s + (c.total || 0), 0);
      const managed = (w.clients || []).reduce((s, c) => s + (c.managed || 0), 0);
      const pendingW = (w.clients || []).reduce((s, c) => s + (c.pending || 0), 0);
      const prev = arr[i - 1];
      const prevTotal = prev ? (prev.clients || []).reduce((s, c) => s + (c.total || 0), 0) : null;
      return {
        weekStart: w.weekStart,
        label: formatWeekLabel(w.weekStart),
        total,
        managed,
        pending: pendingW,
        managedPct: pctOf(managed, total),
        delta: prevTotal == null ? null : total - prevTotal,
      };
    });
  }, [weekly]);
  const weeklyAvgPct = useMemo(() => {
    if (weeklyData.length === 0) return 0;
    return (
      Math.round(
        (weeklyData.reduce((s, w) => s + w.managedPct, 0) / weeklyData.length) * 10
      ) / 10
    );
  }, [weeklyData]);

  // ── Performance de repartidores (top-N por entregados) ────────────────────
  const driverRows = useMemo(
    () =>
      [...(drivers?.drivers || [])]
        .sort((a, b) => (b.totalDelivered || 0) - (a.totalDelivered || 0))
        .slice(0, TOP_N)
        .map((d) => ({
          driverId: d.driverId,
          name: capitalizeWords(d.driverName),
          delivered: d.totalDelivered || 0,
          slPct: Math.round((d.serviceLevelPct || 0) * 10) / 10,
          attentionPct: Math.round((d.attentionLevelPct || 0) * 10) / 10,
          failed: d.failedDeliveries || 0,
          timeInRoute: minutesToHHMM(d.avgTimeInRouteMinutes),
          routeStart: d.avgRouteStartTime || "—",
        })),
    [drivers]
  );
  const driverTotals = drivers?.totals || null;

  // ── Pendientes (breakdown por comuna o cliente) ───────────────────────────
  const pendingGroups = useMemo(() => {
    const entries = pending?.entries || [];
    const map = new Map();
    entries.forEach((e) => {
      const key = pendingMode === "municipality" ? e.municipality : e.companyId;
      const label = pendingMode === "municipality" ? e.municipality : e.businessName;
      if (key == null) return;
      const prev = map.get(key) || { key, label, total: 0 };
      prev.total += e.count || 0;
      map.set(key, prev);
    });
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [pending, pendingMode]);
  const pendingChart = useMemo(
    () =>
      pendingGroups
        .slice(0, TOP_N)
        .map((g) => ({
          name: truncate(g.label, 16),
          fullName: capitalizeWords(g.label),
          count: g.total,
        }))
        .reverse(),
    [pendingGroups]
  );
  const pendingTotal = pending?.total ?? pendingGroups.reduce((a, g) => a + g.total, 0);

  // ── Pago contra entrega ───────────────────────────────────────────────────
  const codData = useMemo(() => {
    if (!cod) return null;
    const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const block = (b) => ({ count: num(b?.count), amount: num(b?.amount) });
    // Los medios desconocidos van al final en vez de al principio (indexOf = -1).
    const ord = (m) => {
      const i = COD_METHOD_ORDER.indexOf(m);
      return i === -1 ? COD_METHOD_ORDER.length : i;
    };

    const inRoute = block(cod.inRoute);
    const collected = block(cod.collected);
    const settled = block(cod.settled);
    const byMethod = (Array.isArray(cod.byMethod) ? cod.byMethod : [])
      .map((m) => ({
        method: m?.method,
        name: codMethodLabel(m?.method, t),
        color: codMethodColor(m?.method, p),
        settles: COD_SETTLED_METHODS.includes(m?.method),
        count: num(m?.count),
        amount: num(m?.amount),
      }))
      .sort((a, b) => ord(a.method) - ord(b.method));

    return {
      currency: typeof cod.currency === "string" ? cod.currency : "",
      inRoute,
      collected,
      settled,
      byMethod,
      // "Hay algo que mostrar": sirve para decidir si la sección se renderiza en
      // un operador que tiene el COD apagado (ver codOffWithActivity).
      hasActivity:
        inRoute.count > 0 || inRoute.amount > 0 || collected.count > 0 || collected.amount > 0,
    };
  }, [cod, p, t]);

  // La sección se muestra si el operador cobra contra entrega, o si NO lo tiene
  // habilitado pero igual hay montos: ese cruce es la única señal visible de que
  // se están cargando pedidos con cobro en un operador que no cobra. Si el COD
  // está apagado y todo es cero, no se muestra nada.
  const codVisible = Boolean(codData && (codEnabled === true || codData.hasActivity));
  const codOffWithActivity = Boolean(codData?.hasActivity && codEnabled === false);
  const codDonut = useMemo(
    () => (codData?.byMethod || []).filter((m) => m.amount > 0),
    [codData]
  );

  // Curva NS/atención: necesita ≥2 días con datos para tener sentido como serie.
  const hasCurve = slSeries.filter((d) => d.sl != null || d.attention != null).length >= 2;

  // ── Render ────────────────────────────────────────────────────────────────
  if (!logisticCompanyId) {
    return (
      <div className="w-full all-orders-container mt-6 ndash">
        <EmptyBlock label={t('Tu usuario no tiene una empresa logística asociada.')} />
      </div>
    );
  }

  return (
    <div className="w-full all-orders-container mt-6 ndash">
      {/* Header */}
      <header className="ndash__header">
        <div className="ndash__titles">
          <h2 className="ndash__title">{t('Dashboard')}</h2>
          <p className="ndash__subtitle">
            {t('Resumen operativo: nivel de servicio, entregas y operación en un vistazo.')}
          </p>
        </div>
        <div className="ndash__controls">
          <div className="ndash__client">
            <SearchableSelect
              options={clientOptions}
              value={selectedClient}
              onChange={(v) => setSelectedClient(v)}
              placeholder={t('Todos los clientes')}
              isClearable
              isDisabled={loading || clients.length === 0}
              noOptionsText={t('Sin resultados')}
              aria-label={t('Filtrar por cliente')}
            />
          </div>
          <DateRangeFilter
            value={preset}
            onChange={handleRange}
            customRange={preset === "custom" ? range : null}
          />
          <button
            type="button"
            className="ndash__refresh"
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            title={t('Actualizar')}
            aria-label={t('Actualizar datos')}
          >
            <RefreshCw size={16} className={refreshing ? "ndash__spin" : ""} />
          </button>
          {lastUpdated && (
            <span className="ndash__updated">
              {t('Actualizado')}{" "}
              <RelativeTime value={lastUpdated} live t={t} />
            </span>
          )}
        </div>
      </header>

      {error && (
        <div className="ndash-alert" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => fetchData(true)}>
            {t('Reintentar')}
          </button>
        </div>
      )}

      {clientFiltered && (
        <div className="ndash-clientnote" role="note">
          <Building2 size={15} />
          <span>
            {t('Mostrando')} <strong>{capitalizeWords(selectedClientName)}</strong>.{" "}
            {t('El estado de pedidos y el volumen reflejan a este cliente; el nivel de servicio, atención y operación se muestran globales — filtrarlos por cliente está en construcción.')}
          </span>
          <button type="button" onClick={() => setSelectedClient("")}>
            {t('Quitar filtro')}
          </button>
        </div>
      )}

      {/* HERO: gauge nivel de servicio + KPIs secundarios */}
      <div className={`ndash-hero${refreshing ? " ndash-hero--refreshing" : ""}`}>
        <div className="ndash-gauge-card">
          <div className="ndash-gauge-card__head">
            <span className="ndash-gauge-card__chip">
              <Target size={16} strokeWidth={2.2} />
            </span>
            <div>
              <div className="ndash-gauge-card__title">
                {t('Nivel de servicio')}
                {clientFiltered && <span className="ndash-globaltag">{t('Global')}</span>}
              </div>
              <div className="ndash-gauge-card__cap">{t('Entregas')} {periodWord} {t('antes de 21:00')}</div>
            </div>
          </div>
          <div
            className="ndash-gauge"
            style={{
              // Usar % en vez de deg evita el seam visible en el border 0/360
              // (Chrome a veces dibuja un cuadrado/triángulo dentro del aro al
              // tope 100% por discontinuidad del gradient).
              background: `conic-gradient(${slGaugeColor} ${Math.min(Math.max(Number(sl.pct) || 0, 0), 100)}%, rgb(var(--bg-subtle)) 0)`,
            }}
          >
            <div className="ndash-gauge__hole">
              <span className="ndash-gauge__value" style={{ color: slGaugeColor }}>
                {loading ? "—" : `${Math.round(sl.pct)}%`}
              </span>
              <span className="ndash-gauge__cap">{t('cumplimiento SLA')}</span>
            </div>
          </div>
          <div className="ndash-gauge-card__foot">
            <strong>{sl.before.toLocaleString()}</strong> {t('de')}{" "}
            <strong>{sl.total.toLocaleString()}</strong> {t('entregas a tiempo')}
          </div>
        </div>

        <div className="ndash-kpis">
          <KpiTile
            icon={CheckCircle2}
            tone="success"
            value={loading ? "—" : `${compliance.pct}%`}
            label={t('Cumplimiento de entrega')}
            sub={`${compliance.delivered.toLocaleString()} ${t('de')} ${compliance.total.toLocaleString()} ${t('recolectados')}`}
          />
          <KpiTile
            icon={Zap}
            tone="warning"
            value={loading ? "—" : `${sameDay.pct}%`}
            label={t('Atención mismo día')}
            sub={`${sameDay.before.toLocaleString()} ${t('de')} ${sameDay.total.toLocaleString()} ${t('antes de 21:00')}`}
          />
          <KpiTile
            icon={Clock}
            tone="brand"
            value={loading ? "—" : avgTime}
            label={t('Tiempo promedio')}
            sub={t('recolección → entrega')}
          />
          <KpiTile
            icon={PackageCheck}
            tone="info"
            value={
              loading
                ? "—"
                : (clientFiltered ? (selectedClientRow?.total || 0) : collectedTotal).toLocaleString()
            }
            label={clientFiltered ? t('Volumen del cliente') : t('Paquetes recolectados')}
            sub={
              clientFiltered
                ? `${(selectedClientRow?.managed || 0).toLocaleString()} ${t('gestionados')} · ${(selectedClientRow?.pending || 0).toLocaleString()} ${t('pendientes')}`
                : rangeLabel
            }
          />
        </div>
      </div>

      {/* Curva de nivel de servicio y atención (tendencia diaria, global).
          Solo aporta en rangos multi-día: con un solo día (default "Hoy") la
          curva nunca tiene los ≥2 puntos que necesita y caería siempre en
          EmptyState. Ocultamos la sección entera en un día para no arrancar con
          bloques vacíos; en multi-día se conserva su propio manejo de vacíos. */}
      {!isSingleDay && (
      <Section
        title={t('Tendencia de nivel de servicio y atención')}
        hint={
          clientFiltered
            ? t('% diario, global (no por cliente)')
            : t('% de cumplimiento por día del período')
        }
        icon={Activity}
        className="ndash-section--chart"
      >
        {loading ? (
          <div className="ndash-skeleton" style={{ height: 260 }} />
        ) : !hasCurve ? (
          <EmptyBlock
            label={
              isSingleDay
                ? t('Selecciona un rango de varios días para ver la tendencia.')
                : t('Sin datos suficientes para la tendencia en este período.')
            }
          />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={slSeries} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={p.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: p.tick, fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: p.tick, fontSize: 11 }}
                width={36}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<ChartTooltip suffix="%" />} cursor={{ stroke: p.grid }} />
              <Legend
                verticalAlign="top"
                height={28}
                iconType="plainline"
                wrapperStyle={{ fontSize: 12, color: p.tick }}
              />
              <Line
                type="monotone"
                dataKey="sl"
                name={t('Nivel de servicio')}
                stroke={p.success}
                strokeWidth={2.5}
                dot={{ r: 2.5, strokeWidth: 0, fill: p.success }}
                activeDot={{ r: 4 }}
                connectNulls
                isAnimationActive={!refreshing}
              />
              <Line
                type="monotone"
                dataKey="attention"
                name={t('Atención mismo día')}
                stroke={p.warning}
                strokeWidth={2.5}
                strokeDasharray="5 4"
                dot={{ r: 2.5, strokeWidth: 0, fill: p.warning }}
                activeDot={{ r: 4 }}
                connectNulls
                isAnimationActive={!refreshing}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Section>
      )}

      {/* Pulso del período */}
      <Section
        title={t('Entregas y recolecciones por franja horaria')}
        hint={
          isSingleDay
            ? t('Entregas por franja · recolecciones acumuladas · 08:00 a 24:00')
            : t('Todos los días del período · entregas por franja · recolecciones acumuladas · 08:00 a 24:00')
        }
        icon={TrendingUp}
        className="ndash-section--chart"
      >
        {loading ? (
          <div className="ndash-skeleton" style={{ height: 240 }} />
        ) : halfHourData.length === 0 ? (
          <EmptyBlock label={t('Sin entregas ni recolecciones registradas en este período.')} />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={halfHourData} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="ndashArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={p.brand} stopOpacity={0.45} />
                  <stop offset="95%" stopColor={p.brand} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={p.grid} vertical={false} />
              <XAxis
                dataKey="time"
                tick={{ fill: p.tick, fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              {/* Dos escalas: el acumulado del día es un orden de magnitud mayor que
                  el conteo por media hora, así que con un eje único el área de
                  entregas quedaría aplastada contra el piso. El eje derecho se pinta
                  del color de su serie para que quede claro cuál mide cada uno. */}
              <YAxis
                yAxisId="left"
                tick={{ fill: p.tick, fontSize: 11 }}
                allowDecimals={false}
                width={32}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: p.info, fontSize: 11 }}
                allowDecimals={false}
                width={40}
              />
              <Tooltip
                content={
                  <ChartTooltip
                    showName
                    // La curva acumulada esconde cuánto entró en cada franja; se
                    // recupera del propio punto, que sí lo trae.
                    detail={(pt) =>
                      pt.dataKey === "collectedCumulative" && pt.payload?.collected > 0
                        ? `+${pt.payload.collected.toLocaleString()} ${t('en esta franja')}`
                        : null
                    }
                  />
                }
                cursor={{ stroke: p.grid }}
              />
              <Legend
                verticalAlign="top"
                height={28}
                wrapperStyle={{ fontSize: 12, color: p.tick }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="delivered"
                name={t('Entregas')}
                stroke={p.brand}
                strokeWidth={2.5}
                fill="url(#ndashArea)"
                isAnimationActive={!refreshing}
              />
              {/* Línea y no área: una curva acumulada rellena taparía las entregas
                  desde el momento en que la supera, que es casi todo el día. */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="collectedCumulative"
                name={t('Recolecciones (acumulado)')}
                stroke={p.info}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={!refreshing}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* Fila: estado de pedidos + NS por tipo */}
      <div className="ndash-grid-2">
        <Section
          title={t('Estado de pedidos creados')}
          hint={
            clientFiltered
              ? `${capitalizeWords(selectedClientName)} · ${rangeLabel}`
              : `${t('Pedidos creados')} ${rangeLabel}`
          }
          icon={PackageCheck}
        >
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 220 }} />
          ) : !statusCounts || statusTotal === 0 ? (
            <EmptyBlock label={t('Sin pedidos en este período.')} />
          ) : (
            <div className="ndash-status">
              <div className="ndash-status__chart">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={statusData.filter((s) => s.value > 0)}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={2}
                      stroke="none"
                      isAnimationActive={!refreshing}
                    >
                      {statusData
                        .filter((s) => s.value > 0)
                        .map((s) => (
                          <Cell key={s.key} fill={s.color} />
                        ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip suffix={` ${t('pedidos')}`} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="ndash-status__center">
                  <span className="ndash-status__total">{statusTotal.toLocaleString()}</span>
                  <span className="ndash-status__total-cap">{t('pedidos')}</span>
                </div>
              </div>
              <ul className="ndash-legend">
                {statusData.map((s) => (
                  <li className="ndash-legend__item" key={s.key}>
                    <span className="ndash-legend__dot" style={{ background: s.color }} />
                    <span className="ndash-legend__name">{s.name}</span>
                    <span className="ndash-legend__val">{s.value.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Section title={t('Nivel de servicio por tipo')} hint={`${t('Entregas')} ${rangeLabel}`} icon={Target}>
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 220 }} />
          ) : slByType.length === 0 ? (
            <EmptyBlock label={t('Sin datos de nivel de servicio.')} />
          ) : (
            <div className="ndash-bars">
              {slByType.map((row) => {
                const c = slColor(row.pct, p);
                return (
                  <div className="ndash-bars__row" key={row.name}>
                    <div className="ndash-bars__head">
                      <span className="ndash-bars__label">{row.name}</span>
                      <span className="ndash-bars__pct" style={{ color: c }}>
                        {row.pct}%
                      </span>
                    </div>
                    <div className="ndash-bars__track">
                      <div
                        className="ndash-bars__fill"
                        style={{ width: `${Math.min(row.pct, 100)}%`, background: c }}
                      />
                    </div>
                    <span className="ndash-bars__sub">
                      {row.before.toLocaleString()} {t('de')} {row.total.toLocaleString()} {t('a tiempo')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {/* Pago contra entrega. Se renderiza si el operador cobra contra entrega o
          si, teniéndolo apagado, igual hay montos (ver codVisible). */}
      {codVisible && (
        <Section
          title={
            <>
              {t('Pago contra entrega')}
              {clientFiltered && <span className="ndash-globaltag">{t('Global')}</span>}
            </>
          }
          hint={`${t('Cobros')} ${rangeLabel}`}
          icon={Banknote}
        >
          {/* Detección: montos a cobrar en un operador que no cobra contra entrega. */}
          {codOffWithActivity && (
            <div className="ndash-clientnote" role="alert">
              <AlertTriangle size={15} style={{ color: p.warning }} />
              <span>
                <strong>
                  {t('Tu empresa tiene el pago contra entrega apagado y aun así hay pedidos con monto a cobrar.')}
                </strong>{" "}
                {t('Revisa la configuración en Ajustes o los montos cargados en esos pedidos.')}
              </span>
            </div>
          )}

          {/* Advertencia 1: stock vivo vs. flujo del rango. Puestos lado a lado sin
              esto, los dos KPIs se leen como partes de un mismo total. */}
          <div className="ndash-clientnote" role="note">
            <Activity size={15} />
            <span>
              <strong>{t('Estos dos números no se suman.')}</strong>{" "}
              {t('«Por cobrar en ruta» es una foto de este momento: pedidos en ruta con el cobro pendiente, sin importar el rango de fechas. «Cobrado» es el flujo del período seleccionado.')}
            </span>
          </div>

          <div className="ndash-kpis">
            <KpiTile
              icon={Truck}
              tone="warning"
              value={loading ? "—" : formatMoney(codData.inRoute.amount, codData.currency)}
              label={t('Por cobrar en ruta')}
              sub={`${codData.inRoute.count.toLocaleString()} ${t('pedidos')} · ${t('en este momento, no del período')}`}
            />
            <KpiTile
              icon={Wallet}
              tone="success"
              value={loading ? "—" : formatMoney(codData.settled.amount, codData.currency)}
              label={t('Cobrado')}
              sub={`${codData.settled.count.toLocaleString()} ${t('cobros')} · ${t('solo efectivo y transferencia al repartidor')}`}
            />
          </div>

          {/* Advertencia 2: de dónde sale "Cobrado" y por qué la dona muestra más. */}
          <div className="ndash-clientnote" role="note" style={{ marginTop: "1rem" }}>
            <Banknote size={15} />
            <span>
              <strong>{t('«Cobrado» es lo que rinde el repartidor al operador:')}</strong>{" "}
              {t('suma solo efectivo y transferencia al repartidor. La transferencia al vendedor va directo a la cuenta del comercio, así que aparece en la distribución de abajo pero no en ese KPI.')}
            </span>
          </div>

          {loading ? (
            <div className="ndash-skeleton" style={{ height: 220 }} />
          ) : codDonut.length === 0 ? (
            <EmptyBlock label={t('Sin cobros registrados en este período.')} />
          ) : (
            <div className="ndash-status">
              <div className="ndash-status__chart">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={codDonut}
                      dataKey="amount"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={2}
                      stroke="none"
                      isAnimationActive={!refreshing}
                    >
                      {codDonut.map((m) => (
                        <Cell key={m.method || m.name} fill={m.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={
                        <ChartTooltip suffix={codData.currency ? ` ${codData.currency}` : ""} />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* El centro es el CONTEO de cobros del período; el anillo reparte el
                    MONTO. Van juntos a propósito: el monto total no entra legible en
                    el hueco de la dona y ya está arriba, en el KPI. */}
                <div className="ndash-status__center">
                  <span className="ndash-status__total">
                    {codData.collected.count.toLocaleString()}
                  </span>
                  <span className="ndash-status__total-cap">{t('cobros')}</span>
                </div>
              </div>
              <ul className="ndash-legend">
                {codData.byMethod.map((m) => (
                  <li className="ndash-legend__item" key={m.method || m.name}>
                    <span className="ndash-legend__dot" style={{ background: m.color }} />
                    <span className="ndash-legend__name">
                      {m.name}
                      {!m.settles && (
                        <span className="ndash-globaltag">{t('no lo recauda el operador')}</span>
                      )}
                    </span>
                    <span className="ndash-legend__val">
                      {formatMoney(m.amount, codData.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {/* Fila: comunas recolectadas + repartidores en ruta */}
      <div className="ndash-grid-2">
        <Section
          title={t('Comunas con más recolección')}
          hint={`Top ${TOP_N} · ${rangeLabel}`}
          icon={MapPin}
        >
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 260 }} />
          ) : topCommunes.length === 0 ? (
            <EmptyBlock label={t('Sin recolecciones en este período.')} />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, topCommunes.length * 32 + 24)}>
              <BarChart
                data={topCommunes}
                layout="vertical"
                margin={{ top: 4, right: 36, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={p.grid} horizontal={false} />
                <XAxis type="number" tick={{ fill: p.tick, fontSize: 11 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: p.tick, fontSize: 11 }}
                  width={120}
                />
                <Tooltip content={<ChartTooltip suffix={` ${t('paquetes')}`} />} cursor={{ fill: "transparent" }} />
                <Bar dataKey="count" fill={p.brand} radius={[0, 5, 5, 0]} isAnimationActive={!refreshing}>
                  <LabelList dataKey="count" position="right" style={{ fill: p.tick, fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          title={t('Repartidores en ruta')}
          hint={`${totalInRoute.toLocaleString()} ${t('paquetes')} · ${t('en este momento')}`}
          icon={Users}
        >
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 260 }} />
          ) : driversInRoute.length === 0 ? (
            <EmptyBlock label={t('Ningún repartidor con paquetes en ruta.')} />
          ) : (
            <ul className="ndash-board">
              {driversInRoute.map((d, i) => (
                <li className="ndash-board__row" key={d.name + i}>
                  <span className={`ndash-board__rank${i < 3 ? " ndash-board__rank--top" : ""}`}>
                    {i + 1}
                  </span>
                  <span className="ndash-board__name">
                    <Truck size={14} strokeWidth={2} />
                    {d.name}
                  </span>
                  <span className="ndash-board__val">{d.count.toLocaleString()} {t('paq.')}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Volumen por cliente (ranking, por cliente — sí filtrable) */}
      <Section
        title={t('Volumen por cliente')}
        hint={
          selectedClient
            ? `${capitalizeWords(selectedClientName)} · ${rangeLabel}`
            : `Top ${TOP_N} · ${rangeLabel}`
        }
        icon={BarChart3}
      >
        {loading ? (
          <div className="ndash-skeleton" style={{ height: 260 }} />
        ) : volumeRanking.length === 0 ? (
          <EmptyBlock label={t('Sin volumen por cliente en este período.')} />
        ) : (
          <>
            <div className="ndash-vol__totals">
              <span className="ndash-vol__legend">
                <span className="ndash-vol__swatch" style={{ background: p.success }} />
                {t('Gestionados')} <strong>{volumeTotals.managed.toLocaleString()}</strong>
              </span>
              <span className="ndash-vol__legend">
                <span className="ndash-vol__swatch" style={{ background: p.error }} />
                {t('Pendientes')} <strong>{volumeTotals.pending.toLocaleString()}</strong>
              </span>
              <span className="ndash-vol__legend ndash-vol__legend--total">
                {t('Total')} <strong>{volumeTotals.total.toLocaleString()}</strong>
              </span>
            </div>
            {isMobile ? (
              <div className="ndash-cards">
                {volumeRanking.map((c, i) => {
                  const active = selectedClient && c.companyId === selectedClient;
                  return (
                    <div
                      key={c.companyId}
                      className={`ndash-card${active ? " ndash-card--active" : ""}`}
                    >
                      <div className="ndash-card__head">
                        <span className="ndash-card__rank">{i + 1}</span>
                        <span className="ndash-card__title">{c.name}</span>
                        <span
                          className="ndash-pill ndash-card__pill"
                          style={{ color: pctTone(c.managedPct, p), borderColor: pctTone(c.managedPct, p) }}
                        >
                          {c.managedPct}%
                        </span>
                      </div>
                      <StackBar managed={c.managed} pending={c.pending} max={volumeMax} colors={p} />
                      <div className="ndash-card__grid">
                        <div className="ndash-card__metric">
                          <span className="ndash-card__label">{t('Gestionados')}</span>
                          <span className="ndash-card__value" style={{ color: p.success }}>{c.managed.toLocaleString()}</span>
                        </div>
                        <div className="ndash-card__metric">
                          <span className="ndash-card__label">{t('Pendientes')}</span>
                          <span className="ndash-card__value" style={{ color: c.pending > 0 ? p.error : "var(--text-tertiary)" }}>{c.pending.toLocaleString()}</span>
                        </div>
                        <div className="ndash-card__metric">
                          <span className="ndash-card__label">{t('Total')}</span>
                          <span className="ndash-card__value ndash-card__value--strong">{c.total.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="ndash-table-scroll">
              <table className="ndash-table">
                <thead>
                  <tr>
                    <th className="ndash-table__rank">#</th>
                    <th>{t('Cliente')}</th>
                    <th className="ndash-table__bar">{t('Distribución')}</th>
                    <th className="ndash-table__num">{t('Gestionados')}</th>
                    <th className="ndash-table__num">{t('Pendientes')}</th>
                    <th className="ndash-table__num">{t('Total')}</th>
                    <th className="ndash-table__num">% {t('Gestión')}</th>
                  </tr>
                </thead>
                <tbody>
                  {volumeRanking.map((c, i) => {
                    const active = selectedClient && c.companyId === selectedClient;
                    return (
                      <tr
                        key={c.companyId}
                        className={active ? "ndash-table__row--active" : ""}
                      >
                        <td className="ndash-table__rank">{i + 1}</td>
                        <td className="ndash-table__name">{c.name}</td>
                        <td className="ndash-table__bar">
                          <StackBar
                            managed={c.managed}
                            pending={c.pending}
                            max={volumeMax}
                            colors={p}
                          />
                        </td>
                        <td className="ndash-table__num" style={{ color: p.success }}>
                          {c.managed.toLocaleString()}
                        </td>
                        <td
                          className="ndash-table__num"
                          style={{ color: c.pending > 0 ? p.error : "var(--text-tertiary)" }}
                        >
                          {c.pending.toLocaleString()}
                        </td>
                        <td className="ndash-table__num ndash-table__num--strong">
                          {c.total.toLocaleString()}
                        </td>
                        <td className="ndash-table__num">
                          <span
                            className="ndash-pill"
                            style={{ color: pctTone(c.managedPct, p), borderColor: pctTone(c.managedPct, p) }}
                          >
                            {c.managedPct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </>
        )}
      </Section>

      {/* Comparativa semanal (volumen agregado por semana ISO, global).
          Requiere varias semanas para comparar: en un solo día (default "Hoy")
          siempre sería un EmptyState, así que ocultamos la sección entera en
          rangos de un día. En multi-día conserva su manejo de vacíos. */}
      {!isSingleDay && (
      <Section
        title={t('Comparativa semanal')}
        hint={
          weeklyData.length > 0
            ? `${t('Promedio de gestión')} ${weeklyAvgPct}%`
            : t('Volumen agregado por semana')
        }
        icon={CalendarRange}
        className="ndash-section--chart"
      >
        {loading ? (
          <div className="ndash-skeleton" style={{ height: 280 }} />
        ) : weeklyData.length === 0 ? (
          <EmptyBlock
            label={
              isSingleDay
                ? t('Selecciona un rango de varias semanas para comparar.')
                : t('Sin datos semanales en este período.')
            }
          />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={weeklyData} margin={{ top: 8, right: 16, left: -12, bottom: 0 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={p.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: p.tick, fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                <YAxis tick={{ fill: p.tick, fontSize: 11 }} allowDecimals={false} width={36} />
                <Tooltip content={<ChartTooltip suffix={` ${t('pedidos')}`} />} cursor={{ fill: "transparent" }} />
                <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: 12, color: p.tick }} />
                <Bar dataKey="managed" name={t('Gestionados')} stackId="w" fill={p.success} isAnimationActive={!refreshing} />
                <Bar dataKey="pending" name={t('Pendientes')} stackId="w" fill={p.error} radius={[4, 4, 0, 0]} isAnimationActive={!refreshing} />
              </BarChart>
            </ResponsiveContainer>
            {isMobile ? (
              <div className="ndash-cards">
                {weeklyData.map((w) => (
                  <div key={w.weekStart} className="ndash-card">
                    <div className="ndash-card__head">
                      <span className="ndash-card__title">{w.label}</span>
                      <span className="ndash-pill ndash-card__pill" style={{ color: pctTone(w.managedPct, p), borderColor: pctTone(w.managedPct, p) }}>
                        {w.managedPct}%
                      </span>
                    </div>
                    <div className="ndash-card__grid">
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Total')}</span>
                        <span className="ndash-card__value ndash-card__value--strong">{w.total.toLocaleString()}</span>
                      </div>
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Var. total')}</span>
                        <span className="ndash-card__value">
                          {w.delta == null ? (
                            <span className="ndash-delta ndash-delta--flat">—</span>
                          ) : (
                            <span className="ndash-delta" style={{ color: w.delta >= 0 ? p.success : p.error }}>
                              {w.delta >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                              {Math.abs(w.delta).toLocaleString()}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Gestionados')}</span>
                        <span className="ndash-card__value" style={{ color: p.success }}>{w.managed.toLocaleString()}</span>
                      </div>
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Pendientes')}</span>
                        <span className="ndash-card__value" style={{ color: w.pending > 0 ? p.error : "var(--text-tertiary)" }}>{w.pending.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="ndash-table-scroll">
              <table className="ndash-table">
                <thead>
                  <tr>
                    <th>{t('Semana')}</th>
                    <th className="ndash-table__num">{t('Total')}</th>
                    <th className="ndash-table__num">{t('Gestionados')}</th>
                    <th className="ndash-table__num">{t('Pendientes')}</th>
                    <th className="ndash-table__num">% {t('Gestión')}</th>
                    <th className="ndash-table__num">{t('Var. total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyData.map((w) => (
                    <tr key={w.weekStart}>
                      <td className="ndash-table__name">{w.label}</td>
                      <td className="ndash-table__num ndash-table__num--strong">{w.total.toLocaleString()}</td>
                      <td className="ndash-table__num" style={{ color: p.success }}>{w.managed.toLocaleString()}</td>
                      <td className="ndash-table__num" style={{ color: w.pending > 0 ? p.error : "var(--text-tertiary)" }}>{w.pending.toLocaleString()}</td>
                      <td className="ndash-table__num">
                        <span className="ndash-pill" style={{ color: pctTone(w.managedPct, p), borderColor: pctTone(w.managedPct, p) }}>
                          {w.managedPct}%
                        </span>
                      </td>
                      <td className="ndash-table__num">
                        {w.delta == null ? (
                          <span className="ndash-delta ndash-delta--flat">—</span>
                        ) : (
                          <span
                            className="ndash-delta"
                            style={{ color: w.delta >= 0 ? p.success : p.error }}
                          >
                            {w.delta >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                            {Math.abs(w.delta).toLocaleString()}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </>
        )}
      </Section>
      )}

      {/* Fila: performance de repartidores + pendientes (breakdown) */}
      <div className="ndash-grid-2">
        <Section
          title={t('Performance de repartidores')}
          hint={
            driverTotals
              ? `${t('Niv. servicio')} ${Math.round((driverTotals.avgServiceLevel || 0) * 10) / 10}% · Top ${TOP_N}`
              : `Top ${TOP_N} · ${rangeLabel}`
          }
          icon={Gauge}
        >
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 260 }} />
          ) : driverRows.length === 0 ? (
            <EmptyBlock label={t('Sin datos de repartidores en este período.')} />
          ) : isMobile ? (
              <div className="ndash-cards">
                {driverRows.map((d, i) => (
                  <div key={d.driverId || d.name + i} className="ndash-card">
                    <div className="ndash-card__head">
                      <span className="ndash-card__rank">{i + 1}</span>
                      <span className="ndash-card__title">
                        <Truck size={13} strokeWidth={2} className="ndash-table__name-icon" />
                        {d.name}
                      </span>
                      <span className="ndash-pill ndash-card__pill" style={{ color: pctTone(d.slPct, p), borderColor: pctTone(d.slPct, p) }}>
                        {d.slPct}%
                      </span>
                    </div>
                    <div className="ndash-card__grid">
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Entregados')}</span>
                        <span className="ndash-card__value ndash-card__value--strong">{d.delivered.toLocaleString()}</span>
                      </div>
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Fallidos')}</span>
                        <span className="ndash-card__value" style={{ color: d.failed > 0 ? p.error : "var(--text-tertiary)" }}>{d.failed.toLocaleString()}</span>
                      </div>
                      <div className="ndash-card__metric">
                        <span className="ndash-card__label">{t('Tiempo ruta')}</span>
                        <span className="ndash-card__value">{d.timeInRoute}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="ndash-table-scroll">
              <table className="ndash-table">
                <thead>
                  <tr>
                    <th className="ndash-table__rank">#</th>
                    <th>{t('Repartidor')}</th>
                    <th className="ndash-table__num">{t('Entregados')}</th>
                    <th className="ndash-table__num">{t('Niv. servicio')}</th>
                    <th className="ndash-table__num">{t('Fallidos')}</th>
                    <th className="ndash-table__num">{t('Tiempo ruta')}</th>
                  </tr>
                </thead>
                <tbody>
                  {driverRows.map((d, i) => (
                    <tr key={d.driverId || d.name + i}>
                      <td className="ndash-table__rank">{i + 1}</td>
                      <td className="ndash-table__name">
                        <Truck size={13} strokeWidth={2} className="ndash-table__name-icon" />
                        {d.name}
                      </td>
                      <td className="ndash-table__num ndash-table__num--strong">{d.delivered.toLocaleString()}</td>
                      <td className="ndash-table__num">
                        <span className="ndash-pill" style={{ color: pctTone(d.slPct, p), borderColor: pctTone(d.slPct, p) }}>
                          {d.slPct}%
                        </span>
                      </td>
                      <td className="ndash-table__num" style={{ color: d.failed > 0 ? p.error : "var(--text-tertiary)" }}>
                        {d.failed.toLocaleString()}
                      </td>
                      <td className="ndash-table__num">{d.timeInRoute}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
        </Section>

        <Section
          title={t('Pendientes por destino')}
          hint={`${pendingTotal.toLocaleString()} ${t('pendientes')} · ${rangeLabel}`}
          icon={Hourglass}
        >
          <div className="ndash-toggle" role="group" aria-label={t('Agrupar pendientes por')}>
            <button
              type="button"
              className={`ndash-toggle__btn${pendingMode === "municipality" ? " ndash-toggle__btn--active" : ""}`}
              onClick={() => setPendingMode("municipality")}
              aria-pressed={pendingMode === "municipality"}
            >
              <MapPin size={13} strokeWidth={2} />
              {t('Comuna')}
            </button>
            <button
              type="button"
              className={`ndash-toggle__btn${pendingMode === "client" ? " ndash-toggle__btn--active" : ""}`}
              onClick={() => setPendingMode("client")}
              aria-pressed={pendingMode === "client"}
            >
              <Building2 size={13} strokeWidth={2} />
              {t('Cliente')}
            </button>
          </div>
          {loading ? (
            <div className="ndash-skeleton" style={{ height: 220 }} />
          ) : pendingChart.length === 0 ? (
            <EmptyBlock label={t('Sin pendientes en este período.')} />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, pendingChart.length * 32 + 24)}>
              <BarChart data={pendingChart} layout="vertical" margin={{ top: 4, right: 36, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={p.grid} horizontal={false} />
                <XAxis type="number" tick={{ fill: p.tick, fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: p.tick, fontSize: 11 }} width={120} />
                <Tooltip content={<ChartTooltip suffix={` ${t('pendientes')}`} />} cursor={{ fill: "transparent" }} />
                <Bar dataKey="count" fill={p.error} radius={[0, 5, 5, 0]} isAnimationActive={!refreshing}>
                  <LabelList dataKey="count" position="right" style={{ fill: p.tick, fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>
    </div>
  );
};

export default NewDashboard;
